/**
 * Line-level discount helpers for POS cart items (stored in `metadata.discount`).
 */

import { extendedPriceFromCartLine, isRingByAmountLine, lineExtendedPriceBase } from '@/utils/ringByAmount'

/** @param {object|null|undefined} item */
export function lineDiscountFromCartItem (item) {
  if (!item) return null
  const d = item.metadata?.discount ?? item.discount ?? null
  if (!d || typeof d !== 'object') return null
  return d
}

/**
 * Dollar amount to subtract from line extended price (price × qty).
 * @param {object|null|undefined} lineDiscount - `{ type: '$'|'%', amount: number }`
 * @param {number} extendedPrice
 */
export function lineDiscountDollarAmount (lineDiscount, extendedPrice) {
  if (!lineDiscount) return 0
  const amount = Number(lineDiscount.amount) || 0
  if (amount <= 0) return 0
  const extended = Number(extendedPrice) || 0
  if (lineDiscount.type === '%') {
    return extended * (amount / 100)
  }
  return amount
}

/**
 * Map a register cart line to sale API discount fields.
 * @param {object|null|undefined} item
 * @returns {{ discountType: string|null, discountAmount: number, lineTotalAfterDiscount: number, extendedPrice: number }}
 */
export function lineDiscountFieldsForSaleItem (item) {
  const extendedPrice = lineExtendedPriceBase(item)
  const lineDiscount = lineDiscountFromCartItem(item)
  const dollarsOff = lineDiscountDollarAmount(lineDiscount, extendedPrice)
  if (!lineDiscount || dollarsOff <= 0) {
    return {
      discountType: null,
      discountAmount: 0,
      lineTotalAfterDiscount: extendedPrice,
      extendedPrice
    }
  }
  const rawAmount = Number(lineDiscount.amount) || 0
  return {
    discountType: lineDiscount.type === '%' ? 'percentage' : 'fixed',
    discountAmount: rawAmount,
    lineTotalAfterDiscount: Math.max(0, extendedPrice - dollarsOff),
    extendedPrice
  }
}

/** @param {object|null|undefined} item */
export function cartLineTotalAfterLineDiscount (item) {
  return lineDiscountFieldsForSaleItem(item).lineTotalAfterDiscount
}

/**
 * Build a create-sale line payload from a register cart row (non-redemption).
 * @param {object} item
 */
export function saleItemPayloadFromCartLine (item) {
  const { discountType, discountAmount, lineTotalAfterDiscount, extendedPrice } =
    lineDiscountFieldsForSaleItem(item)
  const payload = {
    product_id: item.product_id ?? item.metadata?.product_id,
    line_item_id: item.line_item_id,
    quantity: item.quantity,
    price: item.price,
    subtotal: extendedPrice,
    discount_type: discountType,
    discount_amount: discountAmount,
    tax_amount: 0,
    total: lineTotalAfterDiscount,
    name: item.name,
    modifiers: item.metadata?.modifiers || item.modifiers || []
  }
  const feeMode = item.metadata?.convenience_fee_mode
  if (feeMode === 'apply' || feeMode === 'exempt') {
    payload.convenience_fee_mode = feeMode
  }
  if (isRingByAmountLine(item)) {
    payload.extended_price = extendedPriceFromCartLine(item)
  }
  return payload
}

/** Sum post-line-discount extended amounts for non-redemption cart lines. */
export function saleSubtotalFromCartLines (items) {
  return (items || []).reduce((sum, item) => {
    if (item.redeeming_for_points || item.metadata?.redeeming_for_points) {
      return sum
    }
    return sum + lineDiscountFieldsForSaleItem(item).lineTotalAfterDiscount
  }, 0)
}
